import React, { useEffect, useState } from 'react'
import { Helmet } from 'react-helmet-async'
import { Link, useParams } from 'react-router-dom'
import styled, { useTheme } from 'styled-components'
import type { ethers } from 'ethers'
import { Flex, Text, TextVariant } from '@mprolab/uikit'
import MainWrapper from 'layouts/MainWrapper'
import NotFound from 'views/NotFound'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { getSimpleRpcProviderByChainId } from 'utils/providers'
import { reportClientError } from 'utils/reportClientError'
import { BASE_CHAIN_ID } from 'utils/swap/addresses'
import { STOCK_ISSUER, STOCK_QUOTE, stockBySlug } from 'utils/stocks/catalogue'
import { RANGES, fetchCandles, fetchPoolStats, type Candle, type CandleRange, type PoolStats } from 'utils/stocks/candles'
import { feedAgeSeconds, isFeedStale, multiplierToNumber, poolLiquidityUsd } from 'utils/stocks/pricing'
import {
  SCOPE_MEANING,
  SCOPE_ORDER,
  isAccountBlocked,
  readStockPolicy,
  sharesOnePolicy,
  type StockPolicy
} from 'utils/stocks/policy'
import CandleChart from './CandleChart'
import {
  basescanAddress,
  basescanToken,
  describePool,
  formatAge,
  formatBps,
  formatPercent,
  formatUsd,
  formatUsdCompact,
  swapHref
} from './format'
import { useStockMarkets } from './useStockMarkets'
import { SITE_ORIGIN, Skeleton, StockIcon, StockJsonLd, Tone } from './shared'

/**
 * `/stocks/:slug` — one company, in full.
 *
 * The page answers, in this order, the four questions somebody actually has:
 * what does it cost here, what is it worth, who controls the token, and how do
 * I buy it. The third of those is the one nobody else answers, and it is read
 * live rather than described — a page that says "the issuer can freeze wallets"
 * is an opinion; a page that shows the policy id, its administrator and whether
 * your own wallet passes it is a fact.
 */

const Page = styled(Flex)`
  width: 100%;
  flex-direction: column;
  margin: 140px 0 96px;
`

const Crumbs = styled.nav`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray6};
  margin-bottom: 16px;

  a {
    color: ${({ theme }) => theme.colors.gray4};
    text-decoration: none;
  }
  a:hover {
    color: ${({ theme }) => theme.colors.cyan};
  }
`

const Head = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;
  flex-wrap: wrap;
`

const Identity = styled(Flex)`
  align-items: center;
  gap: 14px;
`

const BigIcon = styled(StockIcon)`
  width: 48px;
  height: 48px;
`

const Price = styled.div`
  text-align: right;
  font-variant-numeric: tabular-nums;
`

const PriceValue = styled.div`
  font-size: 34px;
  font-weight: 600;
  line-height: 1.1;
  color: ${({ theme }) => theme.colors.gray1};
`

const Sub = styled.div`
  font-size: 12px;
  color: ${({ theme }) => theme.colors.gray6};
  margin-top: 4px;
`

const Panel = styled(Flex)`
  width: 100%;
  flex-direction: column;
  gap: 12px;
  margin-top: 28px;
  padding: 20px;
  border: 1px solid ${({ theme }) => theme.colors.gray8};
  border-radius: 14px;
  background: ${({ theme }) => theme.colors.gray11};
`

const PanelTitle = styled(Text)`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.gray1};
`

const Ranges = styled(Flex)`
  gap: 6px;
`

const RangeButton = styled.button<{ $active: boolean }>`
  min-height: 32px;
  padding: 0 12px;
  border-radius: 8px;
  border: 1px solid ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray8)};
  background: transparent;
  color: ${({ theme, $active }) => ($active ? theme.colors.cyan : theme.colors.gray4)};
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
`

const Facts = styled.dl`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px 24px;
  margin: 0;

  dt {
    font-size: 11px;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: ${({ theme }) => theme.colors.gray6};
    margin-bottom: 4px;
  }

  dd {
    margin: 0;
    font-size: 14px;
    color: ${({ theme }) => theme.colors.gray1};
    word-break: break-all;
    font-variant-numeric: tabular-nums;
  }

  a {
    color: ${({ theme }) => theme.colors.cyan};
    text-decoration: none;
  }
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 10px 12px;
    text-align: right;
    font-size: 13px;
    border-bottom: 1px solid ${({ theme }) => theme.colors.gray9};
    white-space: nowrap;
  }

  th {
    font-size: 11px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: ${({ theme }) => theme.colors.gray6};
  }

  th:first-child,
  td:first-child {
    text-align: left;
  }

  td {
    color: ${({ theme }) => theme.colors.gray1};
  }

  tbody tr:last-child td {
    border-bottom: none;
  }

  a {
    color: ${({ theme }) => theme.colors.cyan};
    text-decoration: none;
  }
`

const TradeButton = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 48px;
  padding: 0 28px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.cyan};
  color: ${({ theme }) => theme.colors.black};
  font-weight: 600;
  text-decoration: none;
  width: max-content;

  &:hover {
    opacity: 0.88;
  }
`

const HeaderBuy = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 40px;
  margin-top: 12px;
  padding: 0 20px;
  border-radius: 10px;
  background: ${({ theme }) => theme.colors.cyan};
  color: ${({ theme }) => theme.colors.black};
  font-size: 14px;
  font-weight: 600;
  text-decoration: none;

  &:hover {
    opacity: 0.88;
  }
`

const Prose = styled.div`
  max-width: 780px;
  color: ${({ theme }) => theme.colors.gray3};
  font-size: 14px;
  line-height: 1.7;

  p {
    margin: 0 0 12px;
  }

  a {
    color: ${({ theme }) => theme.colors.cyan};
  }
`

const Badge = styled.span<{ $ok: boolean }>`
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 600;
  color: ${({ theme, $ok }) => ($ok ? theme.colors.green : theme.colors.red)};
  border: 1px solid ${({ theme, $ok }) => ($ok ? theme.colors.green : theme.colors.red)};
`

const StockView: React.FC = () => {
  const theme = useTheme()
  const { slug } = useParams<{ slug: string }>()
  const stock = stockBySlug(slug)
  const { account } = useWeb3React()
  const { markets, loading, failed } = useStockMarkets(stock ? [stock.slug] : [])
  const market = markets[0] ?? null
  const pool = market?.quotingPool ?? null

  const [range, setRange] = useState<CandleRange>('1W')
  // `null` means the index could not be reached, which is not the same claim
  // as "this pool has never traded".
  const [candles, setCandles] = useState<Candle[] | null>([])
  const [chartLoading, setChartLoading] = useState(true)
  const [stats, setStats] = useState<PoolStats | null>(null)
  const [policy, setPolicy] = useState<StockPolicy | null>(null)
  const [now, setNow] = useState(() => Date.now())

  useEffect(() => {
    const timer = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(timer)
  }, [])

  const poolAddress = pool?.address ?? null

  useEffect(() => {
    if (!poolAddress) {
      setCandles([])
      setChartLoading(false)
      return undefined
    }
    const controller = new AbortController()
    setChartLoading(true)
    void fetchCandles({ pool: poolAddress, range, signal: controller.signal }).then((next) => {
      if (controller.signal.aborted) return
      setCandles(next)
      setChartLoading(false)
      /*
       * The chart is the one thing on these pages that is not read from Base,
       * so it is the one thing whose failure we would otherwise learn about
       * from a screenshot. `reportClientError` collapses repeats, so a whole
       * session of a dead index costs one line.
       */
      if (next === null) {
        reportClientError({
          message: `stocks.candlesUnavailable: the price index did not answer for ${poolAddress} (${range})`,
          kind: 'transaction',
          handled: true
        })
      }
    })
    return () => controller.abort()
  }, [poolAddress, range])

  useEffect(() => {
    if (!poolAddress) return undefined
    const controller = new AbortController()
    void fetchPoolStats({ pool: poolAddress, signal: controller.signal }).then((next) => {
      if (!controller.signal.aborted) setStats(next)
    })
    return () => controller.abort()
  }, [poolAddress])

  useEffect(() => {
    if (!stock) return undefined
    let cancelled = false
    const provider = getSimpleRpcProviderByChainId(BASE_CHAIN_ID) as ethers.providers.Provider
    void readStockPolicy({ provider, token: stock.address, account })
      .then((next) => {
        if (!cancelled) setPolicy(next)
      })
      .catch((error) => {
        /*
         * The page is still worth reading without the policy panel — but this
         * is the one panel nobody else shows, and a silent failure would make
         * the most valuable thing here quietly stop working. Recorded, not
         * surfaced: a visitor can do nothing about it.
         */
        if (cancelled) return
        reportClientError({
          message: `stocks.policyReadFailed: ${stock.symbol} — ${String(
            (error as { message?: unknown })?.message ?? error
          ).slice(0, 160)}`,
          kind: 'transaction',
          handled: true
        })
      })
    return () => {
      cancelled = true
    }
  }, [stock, account])

  if (!stock) return <NotFound />

  const title = `${stock.ticker} tokenized stock on Base (${stock.symbol}) — live price | MPRO Lab`
  const description = `Live onchain price for ${stock.symbol}, the tokenized ${stock.name} share on Base: the pool price, the Chainlink oracle price, the premium between them, pool depth, issuer controls, and a swap through the MPRO Lab router.`
  const age = feedAgeSeconds(market?.oracleUpdatedAt ?? null, now)
  const multiplier = multiplierToNumber(market?.multiplier ?? null)
  const blocked = policy ? isAccountBlocked(policy) : false
  /*
   * "No pool" and "we could not read the chain" look identical on screen unless
   * the page separates them, and one of the two is a claim about the market
   * that would be false.
   */
  const marketState: 'loading' | 'failed' | 'ready' = market ? 'ready' : failed ? 'failed' : loading ? 'loading' : 'ready'
  const loadingMarket = marketState === 'loading'

  return (
    <>
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={`${SITE_ORIGIN}/stocks/${stock.slug}`} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={`${SITE_ORIGIN}/stocks/${stock.slug}`} />
      </Helmet>
      <StockJsonLd stock={stock} />
      <MainWrapper>
        <Page>
          <Crumbs>
            <Link to="/stocks">Tokenized stocks</Link> · {stock.ticker}
          </Crumbs>

          <Head>
            <Identity>
              <BigIcon src={stock.icon} alt="" />
              <div>
                <Text variant={TextVariant.H5} color={theme.colors.gray1}>
                  {stock.name}
                </Text>
                <Sub>
                  {stock.symbol} · {stock.sector} · Base
                </Sub>
              </div>
            </Identity>
            <Price>
              <PriceValue>
                {marketState === 'loading' ? (
                  <Skeleton $width="170px" $height="30px" />
                ) : marketState === 'failed' ? (
                  'Unavailable'
                ) : market?.poolPrice === null ? (
                  'No pool'
                ) : (
                  formatUsd(market.poolPrice)
                )}
              </PriceValue>
              <Sub>
                {stats === null && marketState !== 'failed' ? (
                  <Skeleton $width="150px" />
                ) : (
                  <>
                    <Tone $value={stats?.change24hPercent ?? null}>
                      {formatPercent(stats?.change24hPercent ?? null)}
                    </Tone>
                    {' · 24 h · '}
                    {stats?.volume24hUsd ? `${formatUsdCompact(stats.volume24hUsd)} traded` : 'volume unknown'}
                  </>
                )}
              </Sub>
              {/*
                A second buy button, beside the price.
                The one under the venue table is where somebody ends up after
                reading; this one is for the person who arrived from a search
                for the ticker, saw the number they came for, and is done.
              */}
              {pool && (
                <HeaderBuy to={swapHref(stock.address, STOCK_QUOTE.address)}>
                  Buy {stock.symbol} with {STOCK_QUOTE.symbol}
                </HeaderBuy>
              )}
            </Price>
          </Head>

          <Panel>
            <Flex justifyContent="space-between" alignItems="center" flexWrap="wrap" style={{ gap: 12 }}>
              <PanelTitle variant={TextVariant.BODY_SMALL}>
                {pool ? (
                  `${stock.symbol} / ${STOCK_QUOTE.symbol} · ${describePool(pool)}`
                ) : loadingMarket ? (
                  <Skeleton $width="240px" />
                ) : marketState === 'failed' ? (
                  'Base could not be read just now'
                ) : (
                  'No pool on Base yet'
                )}
              </PanelTitle>
              <Ranges>
                {(Object.keys(RANGES) as CandleRange[]).map((key) => (
                  <RangeButton key={key} $active={key === range} onClick={() => setRange(key)}>
                    {key}
                  </RangeButton>
                ))}
              </Ranges>
            </Flex>
            <CandleChart
              loading={loadingMarket || (chartLoading && Boolean(pool))}
              candles={candles ?? []}
              emptyMessage={
                marketState === 'failed'
                  ? 'Base could not be read just now, so this page cannot say what this token trades at. It is a failed read, not an empty market — try again in a moment.'
                  : !pool
                    ? `${stock.symbol} has been issued but no ${STOCK_QUOTE.symbol} pool on Base holds it yet, so there is nothing to chart and nothing to swap.`
                    : candles === null
                      ? 'The price index did not answer, so there is no chart right now. Every number outside this panel is read from Base and is unaffected.'
                      : 'No price history for this pool yet.'
              }
            />
          </Panel>

          <Panel>
            <PanelTitle variant={TextVariant.BODY_SMALL}>Price against the oracle</PanelTitle>
            <Facts>
              <div>
                <dt>Pool price</dt>
                <dd>{loadingMarket ? <Skeleton $width="86px" /> : formatUsd(market?.poolPrice ?? null)}</dd>
              </div>
              <div>
                <dt>Chainlink oracle</dt>
                <dd>
                  {loadingMarket ? (
                    <Skeleton $width="86px" />
                  ) : (
                    <>
                      {formatUsd(market?.oraclePrice ?? null)}{' '}
                      <Sub>
                        {isFeedStale(market?.oracleUpdatedAt ?? null, now) ? 'stale · ' : ''}
                        {formatAge(age)}
                      </Sub>
                    </>
                  )}
                </dd>
              </div>
              <div>
                <dt>Premium over oracle</dt>
                <dd>
                  {loadingMarket ? (
                    <Skeleton $width="64px" />
                  ) : (
                    <Tone $value={market?.premiumBps === null ? null : -(market?.premiumBps ?? 0)}>
                      {formatBps(market?.premiumBps ?? null)}
                    </Tone>
                  )}
                </dd>
              </div>
              <div>
                <dt>Multiplier</dt>
                <dd>
                  {loadingMarket ? (
                    <Skeleton $width="80px" />
                  ) : (
                    <>
                      {multiplier === null ? '—' : multiplier.toFixed(6)}
                      <Sub>
                        {multiplier === 1 ? 'one token is one share' : 'adjusted for dividends or splits'}
                      </Sub>
                    </>
                  )}
                </dd>
              </div>
            </Facts>
          </Panel>

          <Panel>
            <PanelTitle variant={TextVariant.BODY_SMALL}>Where it trades on Base</PanelTitle>
            {loadingMarket ? (
              <Table>
                <thead>
                  <tr>
                    <th scope="col">Venue</th>
                    <th scope="col">Depth</th>
                    <th scope="col">{STOCK_QUOTE.symbol}</th>
                    <th scope="col">{stock.symbol}</th>
                    <th scope="col">Pool</th>
                  </tr>
                </thead>
                <tbody>
                  {[0, 1, 2].map((row) => (
                    <tr key={row}>
                      {[168, 64, 76, 72, 72].map((width, index) => (
                        <td key={width + index}>
                          <Skeleton $width={`${width}px`} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : market && market.pools.length > 0 ? (
              <Table>
                <thead>
                  <tr>
                    <th scope="col">Venue</th>
                    <th scope="col">Depth</th>
                    <th scope="col">{STOCK_QUOTE.symbol}</th>
                    <th scope="col">{stock.symbol}</th>
                    <th scope="col">Pool</th>
                  </tr>
                </thead>
                <tbody>
                  {market.pools.map((p) => (
                    <tr key={p.address}>
                      <td>
                        {describePool(p)}
                        {p.address === pool?.address ? ' · quoted here' : ''}
                      </td>
                      <td>{formatUsdCompact(poolLiquidityUsd(p, market.oraclePrice, stock.decimals))}</td>
                      <td>{(Number(p.reserveQuote) / 1e6).toLocaleString('en-US', { maximumFractionDigits: 0 })}</td>
                      <td>
                        {(Number(p.reserveBase) / 10 ** stock.decimals).toLocaleString('en-US', {
                          maximumFractionDigits: 4
                        })}
                      </td>
                      <td>
                        <a href={basescanAddress(p.address)} target="_blank" rel="noreferrer">
                          {p.address.slice(0, 8)}…
                        </a>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            ) : (
              <Prose>
                {/* Loading is the skeleton table above, so only two states reach here. */}
                {marketState === 'ready' ? (
                  <p>
                    No {STOCK_QUOTE.symbol} pool on Base holds {stock.symbol} yet. The token exists and can be
                    held; there is simply nothing to trade against it here.
                  </p>
                ) : (
                  <p>
                    Base could not be read just now, so this page cannot list the pools. Nothing here says there
                    are none.
                  </p>
                )}
              </Prose>
            )}
            {pool && (
              <TradeButton to={swapHref(stock.address, STOCK_QUOTE.address)}>
                Buy {stock.symbol} with {STOCK_QUOTE.symbol}
              </TradeButton>
            )}
          </Panel>

          <Panel>
            <PanelTitle variant={TextVariant.BODY_SMALL}>Issuer controls, read from the chain</PanelTitle>
            <Prose>
              <p>
                {stock.symbol} is a B20 token — a standard built into Base itself rather than a contract somebody
                deployed, which is why its address holds a single byte of code. The standard gives its issuer
                powers an ordinary ERC-20 does not have, and the state below is what those powers are set to right
                now.
              </p>
            </Prose>
            <Facts>
              {SCOPE_ORDER.map((scope) => {
                const id = policy?.scopes[scope] ?? null
                const allowed = policy?.account[scope]
                return (
                  <div key={scope}>
                    <dt>{scope} policy</dt>
                    <dd>
                      {policy === null ? (
                        <Skeleton $width="54px" />
                      ) : id === null ? (
                        'not set on this token'
                      ) : id === 0n ? (
                        'none — unrestricted'
                      ) : (
                        `#${id.toString()}`
                      )}
                      {account && allowed !== undefined && (
                        <>
                          {' '}
                          <Badge $ok={allowed}>{allowed ? 'your wallet passes' : 'your wallet is blocked'}</Badge>
                        </>
                      )}
                      <Sub>{SCOPE_MEANING[scope]}</Sub>
                    </dd>
                  </div>
                )
              })}
              <div>
                <dt>Transfers paused</dt>
                <dd>
                  {policy === null ? (
                    <Skeleton $width="40px" />
                  ) : policy.paused.transfer ? (
                    'yes — nothing can move'
                  ) : (
                    'no'
                  )}
                </dd>
              </div>
              <div>
                <dt>Policy administrator</dt>
                <dd>
                  {policy === null ? (
                    <Skeleton $width="220px" />
                  ) : policy.admin ? (
                    <a href={basescanAddress(policy.admin)} target="_blank" rel="noreferrer">
                      {policy.admin}
                    </a>
                  ) : (
                    'none'
                  )}
                  <Sub>Adds and removes wallets from the list.</Sub>
                </dd>
              </div>
            </Facts>
            <Prose>
              {policy && sharesOnePolicy(policy) && (
                <p>
                  All four scopes point at the same policy, so one list governs sending, receiving, executing and
                  minting for this token.
                </p>
              )}
              {blocked && (
                <p>
                  <strong>Your connected wallet is blocked under at least one scope.</strong> A transfer from it
                  will revert, and a blocked holder&apos;s balance can be burned by the issuer without their
                  cooperation.
                </p>
              )}
              <p>
                What that permits, concretely: a blocked sender cannot sell or move the token; a blocked receiver
                cannot be paid, which also traps the other side of any position that pays out both tokens at once;
                a blocked holder&apos;s balance can be burned outright; and pausing transfers stops every pool at
                the same moment, so nobody can exit. Approvals are deliberately not policy-gated, so an approval
                that succeeds tells you nothing about whether the transfer will.{' '}
                <Link to="/documentation/tokenized-stocks">How tokenized stocks work →</Link>
              </p>
            </Prose>
          </Panel>

          <Panel>
            <PanelTitle variant={TextVariant.BODY_SMALL}>The instrument</PanelTitle>
            <Facts>
              <div>
                <dt>Token</dt>
                <dd>
                  <a href={basescanToken(stock.address)} target="_blank" rel="noreferrer">
                    {stock.address}
                  </a>
                </dd>
              </div>
              <div>
                <dt>Decimals</dt>
                <dd>{stock.decimals}</dd>
              </div>
              <div>
                <dt>Chainlink feed</dt>
                <dd>
                  <a href={basescanAddress(stock.feed)} target="_blank" rel="noreferrer">
                    {stock.feed}
                  </a>
                </dd>
              </div>
              <div>
                <dt>Issuer</dt>
                <dd>
                  {STOCK_ISSUER.name}
                  <Sub>{STOCK_ISSUER.jurisdiction}</Sub>
                </dd>
              </div>
            </Facts>
            <Prose>
              <p>{stock.about}</p>
              <p>
                Holding and trading on a decentralised exchange is permissionless; minting and redeeming are not,
                and happen only between the issuer and its authorised participants. Nothing on this page is advice
                or an offer, and MPRO Lab neither issues nor custodies these tokens — it routes a swap between
                pools that already exist.
              </p>
            </Prose>
          </Panel>
        </Page>
      </MainWrapper>
    </>
  )
}

export default StockView
